import { ArrowLeft } from 'lucide-react'
import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Logo } from '@/components/layout/logo.tsx'
import { GlowBackdrop } from '@/components/ui/decor.tsx'
import { cn } from '@/lib/cn.ts'
import {
  LEGAL_LANGUAGES,
  type LegalDocument,
  type LegalLanguage,
  useLegalLanguage,
} from './legal-document.ts'

const BACK_LABEL: Record<LegalLanguage, string> = {
  id: 'Kembali ke beranda',
  en: 'Back to home',
}

const DOC_LABEL: Record<LegalLanguage, { privacy: string; terms: string }> = {
  id: { privacy: 'Kebijakan Privasi', terms: 'Syarat & Ketentuan' },
  en: { privacy: 'Privacy Policy', terms: 'Terms & Conditions' },
}

function formatEffectiveDate(isoDate: string, language: LegalLanguage): string {
  return new Intl.DateTimeFormat(language === 'id' ? 'id-ID' : 'en-GB', {
    timeZone: 'Asia/Jakarta',
    dateStyle: 'long',
  }).format(new Date(`${isoDate}T00:00:00+07:00`))
}

export interface LegalShellProps {
  /** Both translations; the reader switches between them without navigating. */
  translations: Record<LegalLanguage, LegalDocument>
  /** Which page this is, so the footer can link to the other one. */
  current: 'privacy' | 'terms'
}

/**
 * The frame every legal page shares: language toggle, table of contents, and
 * the numbered clauses themselves.
 *
 * Both languages ship in the bundle rather than behind a route, because the
 * point of the toggle is to re-read the same clause in the other language
 * without losing your place.
 */
export function LegalShell({ translations, current }: LegalShellProps) {
  const { language, setLanguage } = useLegalLanguage()
  const doc = translations[language]

  // A policy is a long read reached from a footer link, so it must not open
  // halfway down where the previous page happened to be scrolled to.
  useEffect(() => {
    window.scrollTo({ top: 0 })
  }, [current])

  useEffect(() => {
    document.title = `${doc.title} · YT Clip AI`
    document.documentElement.lang = language
  }, [doc.title, language])

  const otherPath = current === 'privacy' ? '/terms' : '/privacy'
  const otherLabel =
    current === 'privacy' ? DOC_LABEL[language].terms : DOC_LABEL[language].privacy

  return (
    <div className="min-h-screen bg-surface">
      <header className="sticky top-0 z-40 border-b border-outline-variant bg-surface/80 backdrop-blur-md">
        <div className="mx-auto flex h-16 max-w-3xl items-center justify-between gap-4 px-4">
          <Link to="/" className="rounded-sm focus-visible:outline-2 focus-visible:outline-primary">
            <Logo />
          </Link>
          <LanguageToggle language={language} onChange={setLanguage} />
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 pb-20 pt-10">
        <Link
          to="/"
          className="-ml-3 inline-flex items-center gap-2 rounded-full px-3 py-2 text-label-md font-medium text-primary transition-colors hover:bg-primary/10 focus-visible:outline-2 focus-visible:outline-primary"
        >
          <ArrowLeft className="size-4" aria-hidden="true" />
          {BACK_LABEL[language]}
        </Link>

        <article className="relative mt-4 overflow-hidden rounded-2xl bg-surface-container px-6 py-10 shadow-level-1 md:px-10 md:py-14">
          <GlowBackdrop />

          <div className="relative">
            <h1 className="text-headline-md font-medium leading-tight text-on-surface">
              {doc.title}
            </h1>
            <p className="mt-3 text-body-lg text-on-surface-variant">{doc.subtitle}</p>
            <p className="mt-4 text-label-sm text-on-surface-variant">
              {doc.effectiveLabel} {formatEffectiveDate(doc.effectiveDate, language)}
            </p>

            <p className="mt-8 rounded-md bg-secondary-container p-5 text-body-md text-on-secondary-container">
              {doc.summary}
            </p>

            <nav aria-label={doc.tocLabel} className="mt-8 rounded-md bg-surface-container-low p-5">
              <p className="text-label-md font-medium text-on-surface">{doc.tocLabel}</p>
              <ol className="mt-3 space-y-1.5">
                {doc.sections.map((section, index) => (
                  <li key={section.id} className="text-body-md">
                    <a
                      href={`#${section.id}`}
                      className="rounded-sm text-on-surface-variant underline-offset-4 transition-colors hover:text-primary hover:underline focus-visible:outline-2 focus-visible:outline-primary"
                    >
                      <span className="tabular-nums">{index + 1}.</span> {section.heading}
                    </a>
                  </li>
                ))}
              </ol>
            </nav>

            <div className="mt-12 space-y-10">
              {doc.sections.map((section, index) => (
                <section key={section.id} id={section.id} className="scroll-mt-24">
                  <h2 className="text-title-lg font-medium text-on-surface">
                    <span className="tabular-nums text-primary">{index + 1}.</span>{' '}
                    {section.heading}
                  </h2>

                  <div className="mt-3 space-y-3">
                    {section.blocks.map((block, blockIndex) =>
                      block.kind === 'p' ? (
                        <p
                          key={blockIndex}
                          className="text-body-md leading-relaxed text-on-surface-variant"
                        >
                          {block.text}
                        </p>
                      ) : (
                        <ul key={blockIndex} className="space-y-2 pl-1">
                          {block.items.map((item) => (
                            <li
                              key={item}
                              className="flex gap-3 text-body-md leading-relaxed text-on-surface-variant"
                            >
                              <span
                                aria-hidden="true"
                                className="mt-2.5 size-1.5 shrink-0 rounded-full bg-primary"
                              />
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      ),
                    )}
                  </div>
                </section>
              ))}
            </div>
          </div>
        </article>

        <div className="mt-8 flex justify-center">
          <Link
            to={otherPath}
            className="inline-flex h-12 items-center rounded-full border border-outline px-8 text-label-md font-medium text-primary transition-all duration-300 ease-emphasized hover:bg-primary/5 active:scale-95"
          >
            {otherLabel}
          </Link>
        </div>
      </main>

      <footer className="border-t border-outline-variant py-8">
        <p className="text-center text-label-sm text-on-surface-variant">
          © {new Date().getFullYear()} YT Clip AI
        </p>
      </footer>
    </div>
  )
}

function LanguageToggle({
  language,
  onChange,
}: {
  language: LegalLanguage
  onChange: (next: LegalLanguage) => void
}) {
  return (
    <div
      role="group"
      aria-label={language === 'id' ? 'Pilih bahasa' : 'Choose language'}
      className="inline-flex shrink-0 rounded-full bg-surface-container p-1"
    >
      {LEGAL_LANGUAGES.map((option) => (
        <button
          key={option.id}
          type="button"
          aria-pressed={language === option.id}
          onClick={() => onChange(option.id)}
          className={cn(
            'rounded-full px-4 py-1.5 text-label-md font-medium uppercase transition-all duration-300 ease-emphasized',
            'focus-visible:outline-2 focus-visible:outline-primary',
            language === option.id
              ? 'bg-primary text-on-primary'
              : 'text-on-surface-variant hover:text-on-surface',
          )}
        >
          <span className="sr-only">{option.label}</span>
          <span aria-hidden="true">{option.id}</span>
        </button>
      ))}
    </div>
  )
}
